import React, { useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Icon, InputText, Modal } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { getGetBasketQuantityQueryKey, getGetBasketQueryKey, useUpdateLineDiscount } from '@/generated/basket';
import { useGetTranslations } from '@/generated/translations';
import styles from './DiscountModifier.module.scss';

interface DiscountModifierProps {
	lineId: number;
}

export const DiscountModifier: React.FunctionComponent<DiscountModifierProps> = ({ lineId }) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const [openModal, setOpenModal] = React.useState(false);
	const [discount, setDiscount] = React.useState<number | null>(null);

	const queryClient = useQueryClient();

	const updateLineWithDiscount = useUpdateLineDiscount();

	const editDiscount = () => {
		setOpenModal(true);
	};

	const closeModal = () => {
		setOpenModal(false);
	};

	const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		// Handle the input value change here
		setDiscount(Number(value));
	};

	const onCancel = () => {
		closeModal();
	};

	const onSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		updateLineWithDiscount.mutate(
			{
				segmentationId,
				lineId,
				data: {
					salesRepDiscount: discount ?? 0,
				},
			},
			{
				onSuccess: async (res, variables) => {
					// Invalidate and refetch
					await queryClient.invalidateQueries({
						queryKey: getGetBasketQuantityQueryKey(segmentationId),
					});
					await queryClient.setQueryData(getGetBasketQueryKey(variables.segmentationId), res);
					closeModal();
				},
			},
		);
	};

	return (
		<>
			<Button
				type="button"
				hasOnlyIcon
				variant={'ghost'}
				aria-label={'edit discount'}
				size="sm"
				onClick={editDiscount}
			>
				<Icon name={'pencil'} size="md" />
			</Button>
			<Modal open={openModal} size={'sm'} onClose={closeModal}>
				<form onSubmit={onSubmit} className={styles.form}>
					<h5 className={styles.title}>{translations?.data.basket.salesRepDiscount}</h5>
					<InputText
						label={translations?.data.basket.salesRepDiscountLabel}
						placeholder={'Number in %'}
						type={'number'}
						min={1}
						value={discount || ''}
						max={100}
						onChange={onChangeInput}
						className={styles.input}
					/>
					<div className={styles.buttonWrapper}>
						<Button
							type={'button'}
							variant={'linkButton'}
							size={'md'}
							onClick={onCancel}
							className={styles.button}
						>
							{translations?.data.shared.cancel}
						</Button>
						<Button
							type={'submit'}
							variant={'primary'}
							size={'md'}
							className={styles.button}
							disabled={updateLineWithDiscount.isPending}
						>
							{translations?.data.shared.next}
						</Button>
					</div>
				</form>
			</Modal>
		</>
	);
};
